import { IAuthenticationManager } from '../../interface/authentication';
import { IWorkspaceDB } from '../../interface/db';
import { IOperationManager } from '../../interface/operation';
import { downloadAll } from '../operation/downloadmedia';
import updateSite from '../operation/updatesite';
import { injectFetch, injectZip } from '../../interface/util';
import PPromise from 'p-progress';
import IMediumDto from '../../model/MediumDto.if';
import ILanguageDto from '../../model/LanguageDto.if';
import { IMediaManager } from '../../interface/media';
import { filterAsync } from '../../util/array';
import { invertResult } from '../../util/promise';

interface IOperationManagerPrv extends IOperationManager {

}

function _opUpdateSite(auth: IAuthenticationManager, db: IWorkspaceDB, name: string): PPromise<void> {
    const zip = injectZip();
    const fetcher = injectFetch();

    return updateSite(fetcher, zip, db, auth, name);
}

function _opDownloadMedia(auth: IAuthenticationManager, db: IWorkspaceDB, media: Array<IMediumDto>): PPromise<void> {
    return new PPromise(async function(resolve, reject, progress) {
        try {
            const shouldOpenClose = !db.isOpen();

            if (shouldOpenClose) {
                await db.open();
            }

            const promise = downloadAll(media, auth);
            promise.onProgress(function(data) {
                progress(data);
            });

            await promise;

            if (shouldOpenClose) {
                db.close();
            }

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

function _opGetAllMedia(db: IWorkspaceDB): Promise<Array<IMediumDto>> {
    return new Promise(async function(resolve, reject) {
        try {
            const shouldOpenClose = !db.isOpen();

            if (shouldOpenClose) {
                await db.open();
            }
            const allMedia = await db.medium().all();

            if (shouldOpenClose) {
                db.close();
            }

            resolve(allMedia);
        } catch (error) {
            reject(error);
        }
    });
}

function Operation(auth: IAuthenticationManager, media: IMediaManager, db: IWorkspaceDB, name: string): IOperationManager {
    const manager: IOperationManagerPrv = {
        updateSite: () => _opUpdateSite(auth, db, name),
        downloadAllMediaAllLanguages: () => {
            return new PPromise(function(resolve, reject, progress) {
                _opGetAllMedia(db)
                    .then(allMedia => {
                        const promise = _opDownloadMedia(auth, db, allMedia);
                        promise.onProgress(progressPC => progress(progressPC));
                        return promise;
                    })
                    .then(() => resolve())
                    .catch(error => reject(`Failed to download media: ${error}`));
            });
        },
        downloadAllMediaInLanguage: (language: ILanguageDto) => {
            return new PPromise(function(resolve, reject, progress) {
                media.mediaForLanguage(language)
                    .then(allMedia => filterAsync<IMediumDto>(allMedia, medium => invertResult(media.isMediumCached(medium.id))) )
                    .then(uncachedMedia => {
                        const promise = _opDownloadMedia(auth, db, uncachedMedia);
                        promise.onProgress(progressPC => progress(progressPC));
                        return promise;
                    })
                    .then(() => resolve())
                    .catch(error => reject(`Failed to download media: ${error}`));
            });
        },
        downloadMedia: (media: Array<IMediumDto>) => _opDownloadMedia(auth, db, media),
    };

    return manager as IOperationManager;
}

export default Operation;
